import { ACTS } from "./engine/acts";
import { MONOLITH_ACTS } from "./engine/monolithActs";

/**
 * The front door is a family of scenes, not a single page. Each renders the
 * same portfolio content by entirely different means, and they fall into two
 * kinds:
 *
 *   scroll — a seven-act piece driven by scroll position, sharing the gate,
 *            overlay, act rail and audio.
 *   free   — owns its whole surface: its own layout, its own interaction model,
 *            no acts and no scrollbar. The shell only lends it the top chrome.
 *
 * Everything heavy is lazy, so picking one scene never loads another's code.
 */

export const GROUPS = [
  { id: "immersive", label: "Immersive", note: "Scroll through it" },
  { id: "interactive", label: "Interactive", note: "Play with it" },
];

export const SCENES = [
  {
    id: "particles",
    group: "immersive",
    kind: "scroll",
    label: "Particle Field",
    tag: "GPGPU",
    blurb:
      "A quarter of a million points, simulated on your GPU, reassembling into every section.",
    intro: {
      headline: "Nothing here is a picture.",
      body:
        "Every mark on this screen is a particle, simulated on your GPU, right now. Scroll and it rearranges itself into a portfolio.",
    },
    boot: [
      "allocating particle buffers",
      "compiling simulation shaders",
      "rasterising glyph formations",
      "seeding curl-noise field",
      "warming bloom pyramid",
    ],
    acts: ACTS,
    load: () => import("./engine/Engine"),
  },
  {
    id: "monolith",
    group: "immersive",
    kind: "scroll",
    label: "Monolith",
    tag: "Raymarched",
    blurb:
      "No geometry at all. A world of signed distance fields, lit and marched one ray per pixel.",
    intro: {
      headline: "There is no model here.",
      body:
        "No vertices, no meshes — every surface is solved per pixel by walking a ray until it hits a distance field. Scroll and the world doesn't cut, it becomes the next one.",
    },
    boot: [
      "compiling distance fields",
      "linking march kernel",
      "projecting decal atlas",
      "resolving light transport",
      "warming bloom pyramid",
    ],
    acts: MONOLITH_ACTS,
    load: () => import("./engine/Raymarch"),
  },
  {
    id: "ink",
    group: "interactive",
    kind: "free",
    label: "Ink",
    tag: "Fluid",
    blurb:
      "The whole portfolio on one page, printed in invisible ink. Drag to develop it.",
    boot: [
      "priming velocity field",
      "allocating dye buffers",
      "seeding pressure solver",
      "setting the page",
      "drying the plate",
    ],
    load: () => import("./scenes/InkScene"),
  },
  {
    id: "gallery",
    group: "interactive",
    kind: "free",
    label: "The Gallery",
    tag: "Walkable 3D",
    blurb:
      "A real room, built from geometry. Mirror floor, cast shadows, the work on plinths. Walk it yourself.",
    boot: [
      "tracing letterforms",
      "extruding the sculpture",
      "baking the environment",
      "hanging the exhibits",
      "raising the lights",
    ],
    load: () => import("./scenes/GalleryScene"),
  },
];

export const DEFAULT_SCENE = "particles";

export const getScene = (id) => SCENES.find((s) => s.id === id) || SCENES[0];

export const isSceneId = (id) => SCENES.some((s) => s.id === id);
